import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  Clock,
  MapPin,
  GraduationCap,
  Check,
} from "lucide-react";

const OPEN_ROLES = [
  {
    title: "Marketing Executive",
    department: "Marketing",
    type: "Part-Time / Freelance",
    location: "Remote / Flexible",
    experience: "No experience required",
    about:
      "We are looking for enthusiastic and motivated individuals to join our team as Part-Time / Freelance Marketing Executives. No previous marketing experience is required — we provide guidance and support to help you learn and develop your marketing skills. This role is ideal for students, graduates, career starters, freelancers, or anyone looking for flexible part-time work.",
    responsibilities: [
      "Promote our company and services through online platforms.",
      "Create and share engaging social media content.",
      "Support marketing campaigns and promotional activities.",
      "Identify potential customers and business opportunities.",
      "Assist with lead generation and customer outreach.",
      "Manage and grow social media presence.",
      "Research market trends and potential customers.",
      "Communicate with potential clients professionally.",
      "Support the marketing team with day-to-day activities.",
      "Use AI and digital marketing tools to create content and improve productivity.",
    ],
    lookingFor: [
      "Motivated and willing to learn.",
      "Friendly and confident in communication.",
      "Comfortable using social media.",
      "Creative and interested in digital marketing.",
      "Reliable and able to work independently.",
      "Interested in technology, AI, and business growth.",
    ],
    plusSkills: [
      "Instagram",
      "Facebook",
      "LinkedIn",
      "YouTube",
      "Canva",
      "TikTok",
      "Digital marketing",
      "Content creation",
      "AI tools",
      "Sales or customer service",
    ],
    offer: [
      "Flexible part-time working hours.",
      "Freelance opportunities.",
      "Remote working options.",
      "No previous experience required.",
      "Training and ongoing support.",
      "Opportunity to gain real-world marketing experience.",
      "Opportunity to develop digital marketing and AI skills.",
      "Career growth opportunities based on performance.",
    ],
    whoCanApply: [
      "Students",
      "Graduates",
      "Beginners",
      "Freelancers",
      "Career starters",
      "People looking for part-time work",
      "Anyone interested in marketing and technology",
    ],
  },
];

const JOB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: "Marketing Executive",
  description:
    "<p>We are looking for enthusiastic and motivated individuals to join our team as Part-Time / Freelance Marketing Executives. No previous marketing experience is required — training and guidance will be provided. Ideal for students, graduates, career starters, freelancers, or anyone looking for flexible part-time work.</p>" +
    "<p><strong>Key responsibilities:</strong> promote our company and services online, create and share social media content, support marketing campaigns, identify potential customers, assist with lead generation and outreach, manage and grow social media presence, research market trends, communicate with potential clients professionally, and use AI and digital marketing tools to create content.</p>" +
    "<p><strong>What we offer:</strong> flexible part-time hours, freelance and remote working options, training and ongoing support, real-world marketing experience, and career growth opportunities based on performance.</p>",
  datePosted: "2026-09-06",
  validThrough: "2027-03-06T23:59",
  employmentType: ["PART_TIME", "CONTRACTOR"],
  hiringOrganization: {
    "@type": "Organization",
    name: "Prata Technologies Ltd",
    sameAs: "https://pratatechnologiesltd.com",
  },
  jobLocationType: "TELECOMMUTE",
  applicantLocationRequirements: [
    { "@type": "Country", name: "United Kingdom" },
    { "@type": "Country", name: "India" },
  ],
  identifier: {
    "@type": "PropertyValue",
    name: "Prata Technologies Ltd",
    value: "marketing-executive-part-time-001",
  },
};

const List = ({ title, items, note }) => (
  <div>
    <h3 className="font-display text-base font-medium text-slate-900">{title}</h3>
    {note && <p className="mt-2 text-sm text-slate-500">{note}</p>}
    <ul className="mt-3 space-y-2.5">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-electric" strokeWidth={2} />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const OpenRoles = ({ onApply }) => {
  const [expanded, setExpanded] = useState(null);

  return (
    <div className="space-y-6">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-electric">
        Current Openings
      </p>
      {OPEN_ROLES.map((role) => {
        const isOpen = expanded === role.title;
        return (
          <div
            key={role.title}
            data-testid={`careers-role-${role.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="rounded-3xl border border-electric/20 bg-white p-8 shadow-lg shadow-slate-200/50 md:p-10"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-2xl font-medium text-slate-900">
                  {role.title}
                </h2>
                <p className="mt-1 text-sm text-slate-500">{role.department}</p>
              </div>
              <span className="rounded-full bg-emerald-50 px-4 py-1.5 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                Now Hiring
              </span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2.5">
              {[
                { icon: Clock, label: role.type },
                { icon: MapPin, label: role.location },
                { icon: GraduationCap, label: role.experience },
              ].map((chip) => (
                <span
                  key={chip.label}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium text-slate-700"
                >
                  <chip.icon className="h-3.5 w-3.5 text-electric" strokeWidth={1.5} />
                  {chip.label}
                </span>
              ))}
            </div>

            <p className="mt-5 text-sm leading-relaxed text-slate-600">{role.about}</p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="#apply"
                onClick={() => onApply(role.title)}
                data-testid={`careers-apply-${role.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group inline-flex items-center gap-2 rounded-full bg-electric px-7 py-3.5 text-sm font-medium text-white transition-[background-color,box-shadow,transform] duration-300 hover:scale-[1.02] hover:bg-electric-bright hover:shadow-[0_0_24px_rgba(37,99,235,0.4)] active:scale-95"
              >
                Apply Now
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : role.title)}
                aria-expanded={isOpen}
                data-testid="careers-role-details-toggle"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 text-sm font-medium text-slate-700 transition-[border-color,color] duration-300 hover:border-electric hover:text-electric"
              >
                {isOpen ? "Hide Full Description" : "View Full Description"}
                <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                  <ChevronDown className="h-4 w-4" />
                </motion.span>
              </button>
            </div>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                  data-testid="careers-role-details"
                >
                  <div className="mt-8 grid gap-10 border-t border-slate-200 pt-8 sm:grid-cols-2">
                    <List title="Key Responsibilities" items={role.responsibilities} />
                    <List title="What We Offer" items={role.offer} />
                    <List
                      title="What We're Looking For"
                      note="Previous marketing experience is not required. Training and guidance will be provided."
                      items={role.lookingFor}
                    />
                    <List
                      title="Skills That Are a Plus"
                      note="Helpful, but not essential."
                      items={role.plusSkills}
                    />
                    <List title="Who Can Apply" items={role.whoCanApply} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
      <p className="text-sm text-slate-500">
        Don't see the right role? You're welcome to send a speculative application
        using the same form — we'll keep your details on file for future
        opportunities.
      </p>
    </div>
  );
};

export default OpenRoles;
